import {
  useState, useRef, useEffect, useContext,
} from 'react';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  useLiveAgentSession, useDrawerPrefs, createActionId, getDefaultSession,
  shouldResetSession, reconcileSession, sessionRecord, drawerPrefsRecord,
} from '../platform/session/session';
import { CHANNELS, createBroadcastChannel } from '../platform/events/broadcast';
import { emit } from '../platform/events/lifecycle';
import { emitHeaderEntryClick } from '../shared/analytics';
import { HEADER_ENTRY } from '../shared/assistant-config';

/**
 * The Shell's EAGER half: the drawer open/close/minimize state machine + the FAB's
 * behavior. It lives in the main chunk (not the lazy Shell view) so the FAB and drawer
 * state work before the assistant content loads. App (bootstrap) composes this hook and
 * renders the FAB + lazy Shell from its outputs.
 *
 * It owns `drawerState` (ephemeral) AND the *persisted* drawer-behavior flags
 * (`shouldAutoOpen`, `hasSessionInteractedWithFab`) via the `maa-drawer-prefs` record
 * (Seq B.3c), plus cross-tab drawer-state sync and the auto-open/orientation transitions.
 * It also owns the `maa-session` identity record's lifecycle — seeding it, reconciling it
 * against peer tabs, and renewing it on the reset rules — and cross-READS escalation from
 * `maa-live-agent`, which it never writes.
 *
 * Nothing here mirrors a record in a ref any more. `sessionRecord.get()` / `drawerPrefsRecord
 * .get()` read storage, so they are already the synchronous authority the fab-click actionId
 * and the mount-time reset check need, and `update()` merges onto that current value, so this
 * hook and the FAB cannot revert each other (the WS-3 lost-update hazard is now structural
 * rather than something each writer remembers to guard).
 *
 * Residual coupling (noted for WS-11): drawer control is inseparable from the persisted
 * session because auto-open + fab-interaction must survive reloads/tabs. Modeling those
 * flags as first-class drawer state (rather than session fields) is future Shell work.
 *
 * This hook takes no options: it handles only drawer traffic on the shared assistant
 * BroadcastChannel and relays nothing. (It used to accept an `onBroadcastMessage` escape
 * hatch so App could receive product-pod analytics that pods posted onto the same channel —
 * a same-tab child→ancestor hop routed through the cross-tab bus. Seq D.1 made the pods emit
 * their analytics directly, so the relay is gone.)
 */
export const useDrawerController = () => {
  const { isConsumerApp, customer } = useContext(ExperienceContext);
  // Cross-READ only; liveAgent block is the sole writer. See .claude/context/session-identity.md
  const [liveAgent] = useLiveAgentSession();
  // update() merges onto the record's current value; neither writer can revert the other.
  const [drawerPrefs, updatePrefs] = useDrawerPrefs();
  const { lesser: isMobile } = useBreakpoint('lg');
  // Do not use this setter directly, use updateDrawerState instead.
  const [drawerState, setDrawerState] = useState('CLOSED');
  // Lazy-init so the channel is created once, not re-allocated (and leaked) on every render.
  const broadcastChannel = useRef(null);
  if (!broadcastChannel.current) {
    broadcastChannel.current = createBroadcastChannel(CHANNELS.assistant);
  }
  // Mini drawer is disabled for B2B customers and while escalated to an agent (design decision).
  const enableMiniDrawer = customer?.type !== 'b2b' && !liveAgent?.isEscalated;

  const updateDrawerState = (newDrawerState, { skipBroadcast } = {}) => {
    setDrawerState(newDrawerState);
    if (!skipBroadcast) {
      // App opens new tabs instead of reloading; keep drawer state in sync across app tabs.
      broadcastChannel.current.postMessage({
        type: 'INTERNAL_ONLY_MAA_DRAWER_STATE_CHANGED',
        data: { drawerState: newDrawerState }
      });
    }
  };

  const handleOpen = (isUserInteraction = false, origin = HEADER_ENTRY.ORIGIN_FAB) => {
    const isFromHeader = origin === HEADER_ENTRY.ORIGIN_HEADER;

    if (isUserInteraction && isFromHeader) emitHeaderEntryClick(HEADER_ENTRY.OPEN_LABEL);

    updateDrawerState('OPEN');
    // Read the prior flag before the update, so the fab-click event still fires on the first
    // interaction of the session.
    const alreadyInteracted = drawerPrefsRecord.get()?.hasSessionInteractedWithFab;
    updatePrefs({
      hasSessionInteractedWithFab: alreadyInteracted || isUserInteraction
    });
    // The cadences differ: analytics counts every header ask, fab-click stays once per
    // session. Do not fold them back together.
    if (isUserInteraction && !isFromHeader && !alreadyInteracted) {
      const { sessionId } = sessionRecord.get() ?? {};
      emit('magic-apron-assistant.fab-click', {
        actionID: createActionId(sessionId),
        sessionID: sessionId,
      });
    }
  };

  // Opens this tab directly and broadcasts to peers (deliberate; user-confirmed 2026-07-27).
  // See .claude/context/composition-topology.md and .claude/context/session-identity.md
  const openDrawer = () => {
    handleOpen(false);
    broadcastChannel.current.postMessage({ type: 'MAA_OPEN_DRAWER' });
  };

  // Closing must NOT announce `isReady: false` — see .claude/context/event-bus.md.
  // `origin` is compared strictly since this is also passed straight to a click-event `onClose`.
  const handleClose = (origin = HEADER_ENTRY.ORIGIN_FAB) => {
    if (origin === HEADER_ENTRY.ORIGIN_HEADER) emitHeaderEntryClick(HEADER_ENTRY.CLOSE_LABEL);

    updateDrawerState('CLOSED');
    updatePrefs({ shouldAutoOpen: false });
  };

  // A drag-to-open is a user interaction, so OPEN routes through handleOpen(true).
  const handleDrawerStateChange = (newDrawerState) => {
    if (newDrawerState === 'CLOSED') handleClose();
    if (newDrawerState === 'OPEN') handleOpen(true);
    if (newDrawerState === 'MINIMIZED') {
      updateDrawerState(enableMiniDrawer ? 'MINIMIZED' : 'CLOSED');
      updatePrefs({ shouldAutoOpen: true });
    }
  };

  // Host asks a question on the user's behalf → remember to auto-open next time.
  const markShouldAutoOpen = () => updatePrefs({ shouldAutoOpen: true });

  useEffect(() => {
    // Seed, reconcile, then maybe reset — mount-only. See .claude/context/session-identity.md
    sessionRecord.seed();

    const reconciled = reconcileSession(sessionRecord.get());
    if (reconciled?.sessionId && reconciled.sessionId !== sessionRecord.get()?.sessionId) {
      sessionRecord.set(reconciled);
    }

    const willReset = shouldResetSession(sessionRecord.get());
    const outgoingPrefs = drawerPrefsRecord.get();
    const shouldAutoOpen = willReset
      ? outgoingPrefs.shouldAutoOpen !== false
      : outgoingPrefs?.shouldAutoOpen;

    if (willReset) {
      // maa-live-agent is deliberately NOT reset here — single-writer rule, see live-agent.md
      sessionRecord.set(getDefaultSession());
      drawerPrefsRecord.set({
        shouldAutoOpen,
        hasSessionInteractedWithFab: false,
        pagesExpandedFABShownOn: [],
      });
    }

    if (shouldAutoOpen) {
      let autoOpenState = 'OPEN';
      if (isMobile) autoOpenState = enableMiniDrawer ? 'MINIMIZED' : 'CLOSED';
      updateDrawerState(autoOpenState);
    }

    // Owns only drawer traffic; no longer relays product-pod analytics (see session-identity.md).
    broadcastChannel.current.onmessage = ({ data: { type, data } }) => {
      if (type === 'MAA_OPEN_DRAWER') {
        handleOpen(false);
      } else if (type === 'INTERNAL_ONLY_MAA_DRAWER_STATE_CHANGED' && isConsumerApp) {
        // Cross-tab drawer state synchronization for the mobile app.
        updateDrawerState(data.drawerState, { skipBroadcast: true });
      }
    };

    return () => {
      broadcastChannel.current.close();
    };
  }, []);

  useEffect(() => {
    emit('magic-apron-assistant.drawer-minimize', drawerState);
  }, [drawerState]);

  // On tablets, changing orientation can flip the layout between open and minimized.
  useEffect(() => {
    if (isMobile && drawerState === 'OPEN') {
      updateDrawerState(enableMiniDrawer ? 'MINIMIZED' : 'CLOSED');
    }
    if (!isMobile && drawerState === 'MINIMIZED') {
      updateDrawerState('OPEN');
    }
  }, [isMobile]);

  return {
    drawerState,
    handleOpen,
    handleClose,
    handleDrawerStateChange,
    markShouldAutoOpen,
    openDrawer,
  };
};
